import { BOGGLE_GRID_SIZE } from '~/boggle/config/defaults'
import {
  BOGGLE_PLAYGROUND_LETTER_ADJACENCY_OPTIONS,
  BOGGLE_PLAYGROUND_MIN_WORD_LENGTH_OPTIONS,
  BOGGLE_PLAYGROUND_SKILL_USE_OPTIONS,
  BOGGLE_PLAYGROUND_UNLIMITED_USES,
  DEFAULT_BOGGLE_PLAYGROUND_SETTINGS,
  type BoggleGridDimensions,
  type BoggleGridSize,
  type BoggleLetterAdjacencyMode,
  type BogglePlaygroundComboSettingsInput,
  type BogglePlaygroundComboSettings,
  type BogglePlaygroundMeterLengthBandSettings,
  type BogglePlaygroundMeterSegmentSettings,
  type BogglePlaygroundMeterBurstEffect,
  type BogglePlaygroundMeterBurstSettings,
  type BogglePlaygroundMeterSettings,
  type BogglePlaygroundMeterSettingsInput,
  type BogglePlaygroundMeterWordChangeSettingsInput,
  type BogglePlaygroundMeterWordChangeSettings,
  type BogglePlaygroundResetSkillSettings,
  type BogglePlaygroundSettings,
  type BogglePlaygroundSettingsInput,
  type BogglePlaygroundSkillSettings,
  type BoggleScoreMode,
} from '~/boggle/config/playground'
import {
  BOGGLE_SPECIAL_TILE_KINDS,
  clampBoggleSpecialTileCount,
  getBoggleMaxSpecialTileCount,
  normalizeNullableSpawnCadence,
  normalizeNullableSpecialTileConvertCount,
  normalizeNullableSpecialTileMaximumCount,
  normalizeNullableSpecialTileSpawnChance,
  type BogglePlaygroundSpecialTileSettings,
  type BoggleSpecialTileKind,
  type BoggleSpecialTileSettings,
} from '~/boggle/engine/specialTiles'

function normalizeBoolean(value: boolean | undefined, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback
}

function normalizeLetterAdjacencyMode(
  value: BoggleLetterAdjacencyMode | undefined,
  fallback: BoggleLetterAdjacencyMode,
) {
  return BOGGLE_PLAYGROUND_LETTER_ADJACENCY_OPTIONS.includes(
    value as BoggleLetterAdjacencyMode,
  )
    ? value!
    : fallback
}

function normalizePresetNumber(
  value: number | null | undefined,
  allowed: readonly number[],
  fallback: number,
) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return allowed.includes(value) ? value : fallback
}

function parseGridSize(value: BoggleGridSize | number | undefined) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const normalized = Math.max(1, Math.floor(value))
    return { cols: normalized, rows: normalized } satisfies BoggleGridDimensions
  }

  if (typeof value !== 'string') {
    return null
  }

  const match = /^(\d+)x(\d+)$/i.exec(value.trim())
  if (!match) {
    return null
  }

  const cols = Number(match[1])
  const rows = Number(match[2])
  if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols <= 0 || rows <= 0) {
    return null
  }

  return { cols, rows } satisfies BoggleGridDimensions
}

export function toBoggleGridSize({ cols, rows }: BoggleGridDimensions): BoggleGridSize {
  return `${cols}x${rows}`
}

export function getBoggleGridDimensions(gridSize: BoggleGridSize | number) {
  return parseGridSize(gridSize) ?? parseGridSize(BOGGLE_GRID_SIZE)!
}

export function formatBoggleGridSizeLabel(gridSize: BoggleGridSize | number) {
  const { cols, rows } = getBoggleGridDimensions(gridSize)
  return `${cols} x ${rows}`
}

function normalizeGridSize(
  value: BoggleGridSize | number | undefined,
  fallback: BoggleGridSize,
) {
  const parsed = parseGridSize(value)
  if (!parsed) {
    return fallback
  }

  return toBoggleGridSize(parsed)
}

function normalizeIntervalCount(
  value: null | number | undefined,
  fallback: null | number,
) {
  if (value === null) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(1, Math.floor(value))
}

function normalizeIntervalTimeSeconds(
  value: null | number | undefined,
  fallback: number,
) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(1, Math.floor(value))
}

function normalizeExtraLetterBonusTime(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(0, Math.floor(value))
}

function normalizeScoreBonusMaxLength(
  value: null | number | undefined,
  fallback: null | number,
  minWordLength: number,
  maxWordLength: number,
) {
  if (value === null) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(minWordLength, Math.min(maxWordLength, Math.floor(value)))
}

function normalizeNonNegativeInteger(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(0, Math.floor(value))
}

function normalizeNullableNonNegativeInteger(
  value: null | number | undefined,
  fallback: null | number,
) {
  if (value === null) {
    return null
  }

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(0, Math.floor(value))
}

function normalizeNonNegativeNumber(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(0, value)
}

function normalizePositiveNumber(value: number | undefined, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }

  return Math.max(Number.EPSILON, value)
}

function normalizeScoreMode(
  value: unknown,
  fallback: BoggleScoreMode,
): BoggleScoreMode {
  if (value === 'fixed-one' || value === 'per-cleared-tile') {
    return value
  }

  return value === 'length-minus-two' ? 'per-cleared-tile' : fallback
}

const METER_BURST_EFFECTS = [
  'pause-tile-spawn',
  'replace-interval-timer',
  'virtual-gold',
] as const satisfies readonly BogglePlaygroundMeterBurstEffect[]

const DEFAULT_METER_BURST_TEMPLATE: BogglePlaygroundMeterBurstSettings = {
  drain: {
    drainRampSeconds: 2,
    durationSeconds: 8,
  },
  effects: [],
  enabled: false,
  id: 'burst',
  trigger: 'meter-full',
}

function normalizeMeterBurstTrigger(
  value: unknown,
  fallback: BogglePlaygroundMeterBurstSettings['trigger'],
) {
  return value === 'meter-full' || value === 'timer-end' ? value : fallback
}

function normalizeMeterBurstEffect(value: unknown): BogglePlaygroundMeterBurstEffect | null {
  return typeof value === 'string' &&
    (METER_BURST_EFFECTS as readonly string[]).includes(value)
    ? (value as BogglePlaygroundMeterBurstEffect)
    : null
}

function normalizeMeterBurst(
  input: Partial<BogglePlaygroundMeterBurstSettings> | undefined,
  fallback: BogglePlaygroundMeterBurstSettings,
): BogglePlaygroundMeterBurstSettings {
  const effectsSource = input?.effects ?? fallback.effects
  const effects = effectsSource
    .map(effect => normalizeMeterBurstEffect(effect))
    .filter((effect): effect is BogglePlaygroundMeterBurstEffect => effect !== null)

  return {
    drain: {
      drainRampSeconds: normalizeNonNegativeNumber(
        input?.drain?.drainRampSeconds,
        fallback.drain.drainRampSeconds,
      ),
      durationSeconds: normalizePositiveNumber(
        input?.drain?.durationSeconds,
        fallback.drain.durationSeconds,
      ),
    },
    effects: effects.length > 0 ? effects : [...fallback.effects],
    enabled: normalizeBoolean(input?.enabled, fallback.enabled),
    id: typeof input?.id === 'string' && input.id.trim().length > 0
      ? input.id.trim()
      : fallback.id,
    trigger: normalizeMeterBurstTrigger(input?.trigger, fallback.trigger),
  }
}

function normalizeMeterBursts(
  input: readonly Partial<BogglePlaygroundMeterBurstSettings>[] | undefined,
  fallback: readonly BogglePlaygroundMeterBurstSettings[],
): readonly BogglePlaygroundMeterBurstSettings[] {
  if (input === undefined) {
    return [...fallback]
  }

  if (input.length === 0) {
    return []
  }

  return input.map((burst, index) =>
    normalizeMeterBurst(burst, fallback[index] ?? DEFAULT_METER_BURST_TEMPLATE),
  )
}

function normalizeTileSpawnKind(
  value: unknown,
  fallback: BogglePlaygroundMeterSettings['tileSpawn']['tileKind'],
): BogglePlaygroundMeterSettings['tileSpawn']['tileKind'] {
  return typeof value === 'string' &&
    (BOGGLE_SPECIAL_TILE_KINDS as readonly string[]).includes(value)
    ? (value as BogglePlaygroundMeterSettings['tileSpawn']['tileKind'])
    : fallback
}

function normalizeMeterLetters(
  value: readonly string[] | undefined,
  fallback: readonly string[],
) {
  const source = value && value.length > 0 ? value : fallback
  const letters: string[] = []
  const seen = new Set<string>()

  for (const raw of source) {
    const letter = String(raw).trim().toUpperCase()
    if (!/^[A-Z]$/.test(letter) || seen.has(letter)) {
      continue
    }

    letters.push(letter)
    seen.add(letter)
  }

  return letters.length > 0 ? letters : fallback
}

function getLegacyBoardClearBonusPoints(
  input: BogglePlaygroundComboSettingsInput | undefined,
) {
  if (
    typeof input?.boardClearBonus !== 'number' ||
    !Number.isFinite(input.boardClearBonus)
  ) {
    return undefined
  }

  const normalized = Math.max(0, Math.floor(input.boardClearBonus))
  return normalized > 0 ? normalized : undefined
}

function normalizeComboSettings(
  input: BogglePlaygroundComboSettingsInput | undefined,
  fallback: BogglePlaygroundComboSettings,
): BogglePlaygroundComboSettings {
  return {
    enabled: normalizeBoolean(input?.enabled, fallback.enabled),
    startingValue: normalizeNonNegativeInteger(
      input?.startingValue,
      fallback.startingValue,
    ),
    wordIncrement: normalizeNonNegativeInteger(
      input?.wordIncrement,
      fallback.wordIncrement,
    ),
  }
}

function normalizeMeterLengthBandSettings(
  input: Partial<BogglePlaygroundMeterLengthBandSettings> | undefined,
  fallback: BogglePlaygroundMeterLengthBandSettings,
): BogglePlaygroundMeterLengthBandSettings {
  const minLength = Math.max(
    1,
    normalizeNonNegativeInteger(input?.minLength, fallback.minLength),
  )
  let maxLength: null | number

  if (input?.maxLength === null) {
    maxLength = null
  } else if (typeof input?.maxLength === 'number' && Number.isFinite(input.maxLength)) {
    maxLength = Math.max(minLength, Math.floor(input.maxLength))
  } else {
    maxLength =
      fallback.maxLength === null ? null : Math.max(minLength, fallback.maxLength)
  }

  return {
    base: normalizeNonNegativeNumber(input?.base, fallback.base),
    letterOffset:
      typeof input?.letterOffset === 'number' && Number.isFinite(input.letterOffset)
        ? input.letterOffset
        : fallback.letterOffset,
    maxLength,
    minLength,
    perLetter: normalizeNonNegativeNumber(input?.perLetter, fallback.perLetter),
  }
}

function normalizeMeterLengthBands(
  input: readonly Partial<BogglePlaygroundMeterLengthBandSettings>[] | undefined,
  fallback: BogglePlaygroundMeterWordChangeSettings,
) {
  const source = input && input.length > 0 ? input : fallback.lengthBands
  const defaultBandFallback = {
    base: fallback.base,
    letterOffset: fallback.letterOffset,
    maxLength: null,
    minLength: 1,
    perLetter: fallback.perLetter,
  } satisfies BogglePlaygroundMeterLengthBandSettings

  return source.map((band, index) =>
    normalizeMeterLengthBandSettings(
      band,
      fallback.lengthBands[index] ?? defaultBandFallback,
    ),
  )
}

function normalizeMeterWordChangeSettings(
  input: BogglePlaygroundMeterWordChangeSettingsInput | undefined,
  fallback: BogglePlaygroundMeterWordChangeSettings,
): BogglePlaygroundMeterWordChangeSettings {
  return {
    base: normalizeNonNegativeNumber(input?.base, fallback.base),
    letterOffset:
      typeof input?.letterOffset === 'number' && Number.isFinite(input.letterOffset)
        ? input.letterOffset
        : fallback.letterOffset,
    lengthBands: normalizeMeterLengthBands(input?.lengthBands, fallback),
    perLetter: normalizeNonNegativeNumber(input?.perLetter, fallback.perLetter),
    repeatScalar: normalizeNonNegativeNumber(input?.repeatScalar, fallback.repeatScalar),
    uncommonLetter: {
      letters: normalizeMeterLetters(
        input?.uncommonLetter?.letters,
        fallback.uncommonLetter.letters,
      ),
      perOccurrence: normalizeNonNegativeNumber(
        input?.uncommonLetter?.perOccurrence,
        fallback.uncommonLetter.perOccurrence,
      ),
    },
    rareLetter: {
      letters: normalizeMeterLetters(
        input?.rareLetter?.letters,
        fallback.rareLetter.letters,
      ),
      perOccurrence: normalizeNonNegativeNumber(
        input?.rareLetter?.perOccurrence,
        fallback.rareLetter.perOccurrence,
      ),
    },
  }
}

function normalizeMeterSegmentSettings(
  input: Partial<BogglePlaygroundMeterSegmentSettings> | undefined,
  fallback: BogglePlaygroundMeterSegmentSettings,
): BogglePlaygroundMeterSegmentSettings {
  return {
    scoreBonus: normalizeNonNegativeInteger(input?.scoreBonus, fallback.scoreBonus),
    size: normalizePositiveNumber(input?.size, fallback.size),
  }
}

function normalizeMeterSegments(
  input: readonly Partial<BogglePlaygroundMeterSegmentSettings>[] | undefined,
  fallback: readonly BogglePlaygroundMeterSegmentSettings[],
) {
  const source = input && input.length > 0 ? input : fallback

  return source.map((segment, index) =>
    normalizeMeterSegmentSettings(
      segment,
      fallback[index] ?? fallback[fallback.length - 1]!,
    ),
  )
}

type LegacyMeterSettingsInput = BogglePlaygroundMeterSettingsInput & {
  fullMeterGold?: Partial<{
    baseIntervalSeconds: number
    efficacySpillTau: number
    enabled: boolean
    minimumIntervalSeconds: number
  }>
  goldRush?: Partial<{
    drainRampSeconds: number
    durationSeconds: number
    enabled: boolean
    trigger: 'meter-full' | 'timer-end'
  }>
}

function resolveLegacyMeterTileSpawnInput(
  input: LegacyMeterSettingsInput | undefined,
  fallback: BogglePlaygroundMeterSettings['tileSpawn'],
): BogglePlaygroundMeterSettingsInput['tileSpawn'] | undefined {
  if (input?.tileSpawn !== undefined) {
    return input.tileSpawn
  }

  const legacy = input?.fullMeterGold
  if (legacy === undefined) {
    return undefined
  }

  return {
    baseIntervalSeconds: legacy.baseIntervalSeconds,
    enabled: legacy.enabled,
    powerExponent: fallback.powerExponent,
    tileKind: fallback.tileKind,
  }
}

function resolveLegacyMeterBurstsInput(
  input: LegacyMeterSettingsInput | undefined,
): BogglePlaygroundMeterSettingsInput['bursts'] | undefined {
  if (input?.bursts !== undefined && input.bursts.length > 0) {
    return input.bursts
  }

  const legacy = input?.goldRush
  if (legacy === undefined || !legacy.enabled) {
    return input?.bursts
  }

  return [
    {
      drain: {
        drainRampSeconds: legacy.drainRampSeconds ?? 2,
        durationSeconds: legacy.durationSeconds ?? 8,
      },
      effects: ['virtual-gold', 'pause-tile-spawn', 'replace-interval-timer'],
      enabled: true,
      id: 'gold-rush',
      trigger:
        legacy.trigger === 'meter-full' || legacy.trigger === 'timer-end'
          ? legacy.trigger
          : 'timer-end',
    },
  ]
}

function normalizeMeterSettings(
  input: BogglePlaygroundMeterSettingsInput | undefined,
  fallback: BogglePlaygroundMeterSettings,
): BogglePlaygroundMeterSettings {
  const legacyInput = input as LegacyMeterSettingsInput | undefined
  const capacity = normalizePositiveNumber(input?.capacity, fallback.capacity)
  const tileSpawnInput = resolveLegacyMeterTileSpawnInput(legacyInput, fallback.tileSpawn)
  const burstsInput = resolveLegacyMeterBurstsInput(legacyInput)

  return {
    capacity,
    decrease: {
      backspace: normalizeNonNegativeNumber(
        input?.decrease?.backspace,
        fallback.decrease.backspace,
      ),
      clearSelection: normalizeNonNegativeNumber(
        input?.decrease?.clearSelection,
        fallback.decrease.clearSelection,
      ),
      invalidWord: normalizeNonNegativeNumber(
        input?.decrease?.invalidWord,
        fallback.decrease.invalidWord,
      ),
    },
    enabled: normalizeBoolean(input?.enabled, fallback.enabled),
    tileSpawn: {
      baseIntervalSeconds: normalizePositiveNumber(
        tileSpawnInput?.baseIntervalSeconds,
        fallback.tileSpawn.baseIntervalSeconds,
      ),
      enabled: normalizeBoolean(tileSpawnInput?.enabled, fallback.tileSpawn.enabled),
      powerExponent: normalizePositiveNumber(
        tileSpawnInput?.powerExponent,
        fallback.tileSpawn.powerExponent,
      ),
      tileKind: normalizeTileSpawnKind(tileSpawnInput?.tileKind, fallback.tileSpawn.tileKind),
    },
    bursts: normalizeMeterBursts(burstsInput, fallback.bursts),
    increase: {
      acceptedWord: normalizeMeterWordChangeSettings(
        input?.increase?.acceptedWord,
        fallback.increase.acceptedWord,
      ),
    },
    naturalDecayPerSecond: normalizeNonNegativeNumber(
      input?.naturalDecayPerSecond,
      fallback.naturalDecayPerSecond,
    ),
    segments: normalizeMeterSegments(input?.segments, fallback.segments),
    startingValue: Math.min(
      capacity,
      normalizeNonNegativeNumber(input?.startingValue, fallback.startingValue),
    ),
  }
}

function normalizeSkillSettings(
  input: Partial<BogglePlaygroundSkillSettings> | undefined,
  fallback: BogglePlaygroundSkillSettings,
): BogglePlaygroundSkillSettings {
  const enabled = normalizeBoolean(input?.enabled, fallback.enabled)
  let uses: null | number

  if (input?.uses === null) {
    uses = null
  } else if (typeof input?.uses === 'number' && Number.isFinite(input.uses)) {
    uses = BOGGLE_PLAYGROUND_SKILL_USE_OPTIONS.includes(input.uses as any)
      ? input.uses
      : (fallback.uses ?? null)
  } else {
    uses = fallback.uses ?? null
  }

  return {
    enabled,
    uses,
  }
}

function normalizeResetSkillSettings(
  input: Partial<BogglePlaygroundResetSkillSettings> | undefined,
  fallback: BogglePlaygroundResetSkillSettings,
): BogglePlaygroundResetSkillSettings {
  const normalized = normalizeSkillSettings(input, fallback)
  let delaySeconds: null | number

  if (input?.delaySeconds === null) {
    delaySeconds = null
  } else if (
    typeof input?.delaySeconds === 'number' &&
    Number.isFinite(input.delaySeconds)
  ) {
    delaySeconds = Math.max(1, Math.floor(input.delaySeconds))
  } else {
    delaySeconds = fallback.delaySeconds
  }

  return {
    ...normalized,
    delaySeconds,
  }
}

function normalizeSpecialTileSettings(
  input: Partial<BogglePlaygroundSpecialTileSettings> | undefined,
  fallback: BogglePlaygroundSpecialTileSettings,
  maxCount: number,
): BogglePlaygroundSpecialTileSettings {
  function pickInputOrFallback<Value>(
    inputValue: Value | undefined,
    fallbackValue: Value,
  ) {
    return inputValue === undefined ? fallbackValue : inputValue
  }

  function normalizeTileKind(kind: BoggleSpecialTileKind): BoggleSpecialTileSettings {
    const nextInput = input?.[kind]
    const nextFallback = fallback[kind]
    const maximumCount = normalizeNullableSpecialTileMaximumCount(
      pickInputOrFallback(nextInput?.maximumCount, nextFallback.maximumCount),
      maxCount,
    )
    const resolvedMaxCount = maximumCount ?? maxCount

    return {
      clearOnUse: normalizeBoolean(
        nextInput?.clearOnUse,
        nextFallback.clearOnUse ?? false,
      ),
      clearOnDisuse: normalizeBoolean(
        nextInput?.clearOnDisuse,
        nextFallback.clearOnDisuse ?? false,
      ),
      countAtStart: clampBoggleSpecialTileCount(
        pickInputOrFallback(nextInput?.countAtStart, nextFallback.countAtStart ?? 0),
        resolvedMaxCount,
      ),
      convertXTilesOnSubmit: normalizeNullableSpecialTileConvertCount(
        pickInputOrFallback(
          nextInput?.convertXTilesOnSubmit,
          nextFallback.convertXTilesOnSubmit,
        ),
        maxCount,
      ),
      enabled: normalizeBoolean(nextInput?.enabled, nextFallback.enabled ?? false),
      maximumCount,
      persistDuringBoardClears: normalizeBoolean(
        nextInput?.persistDuringBoardClears,
        nextFallback.persistDuringBoardClears ?? false,
      ),
      replaceWhenCleared: normalizeBoolean(
        nextInput?.replaceWhenCleared,
        nextFallback.replaceWhenCleared ?? false,
      ),
      resetWhenEmpty: normalizeBoolean(
        nextInput?.resetWhenEmpty,
        nextFallback.resetWhenEmpty ?? false,
      ),
      spawnChancePercent: normalizeNullableSpecialTileSpawnChance(
        pickInputOrFallback(
          nextInput?.spawnChancePercent,
          nextFallback.spawnChancePercent,
        ),
      ),
      spawnXTilesOnSubmit: normalizeNullableSpecialTileConvertCount(
        pickInputOrFallback(
          nextInput?.spawnXTilesOnSubmit,
          nextFallback.spawnXTilesOnSubmit,
        ),
        maxCount,
      ),
      spawnEveryXTiles: normalizeNullableSpawnCadence(
        pickInputOrFallback(nextInput?.spawnEveryXTiles, nextFallback.spawnEveryXTiles),
      ),
      spreadToAllAdjacentOnUse: normalizeBoolean(
        nextInput?.spreadToAllAdjacentOnUse,
        nextFallback.spreadToAllAdjacentOnUse ?? false,
      ),
      spreadToOneAdjacentOnDisuse: normalizeBoolean(
        nextInput?.spreadToOneAdjacentOnDisuse,
        nextFallback.spreadToOneAdjacentOnDisuse ?? false,
      ),
    }
  }

  return Object.fromEntries(
    BOGGLE_SPECIAL_TILE_KINDS.map(kind => [kind, normalizeTileKind(kind)]),
  ) as BogglePlaygroundSpecialTileSettings
}

export function normalizeBogglePlaygroundSettings(
  input: BogglePlaygroundSettingsInput = {},
  base: BogglePlaygroundSettings = DEFAULT_BOGGLE_PLAYGROUND_SETTINGS,
): BogglePlaygroundSettings {
  const gridSize = normalizeGridSize(input.gridSize, base.gridSize)
  const { cols, rows } = getBoggleGridDimensions(gridSize)
  const maxWordLength = cols * rows
  const maxSpecialTileCount = getBoggleMaxSpecialTileCount(cols * rows)
  const minWordLength = normalizePresetNumber(
    input.minWordLength,
    BOGGLE_PLAYGROUND_MIN_WORD_LENGTH_OPTIONS,
    base.minWordLength,
  )
  const legacyBoardClearBonusPoints =
    input.boardClearBonusPoints === undefined
      ? getLegacyBoardClearBonusPoints(input.combo)
      : undefined

  return {
    allowDiagonals: normalizeBoolean(input.allowDiagonals, base.allowDiagonals),
    allowRepeats: normalizeBoolean(input.allowRepeats, base.allowRepeats),
    boardClearBonusPoints: normalizeNullableNonNegativeInteger(
      input.boardClearBonusPoints ?? legacyBoardClearBonusPoints,
      base.boardClearBonusPoints,
    ),
    combo: normalizeComboSettings(input.combo, base.combo),
    extraLetterBonusTime: normalizeExtraLetterBonusTime(
      input.extraLetterBonusTime,
      base.extraLetterBonusTime,
    ),
    endIntervalOnSubmit: normalizeBoolean(
      input.endIntervalOnSubmit,
      base.endIntervalOnSubmit,
    ),
    gridSize,
    intervalCount: normalizeIntervalCount(input.intervalCount, base.intervalCount),
    intervalTimeSeconds: normalizeIntervalTimeSeconds(
      input.intervalTimeSeconds,
      base.intervalTimeSeconds,
    ),
    letterAdjacencyMode: normalizeLetterAdjacencyMode(
      input.letterAdjacencyMode,
      base.letterAdjacencyMode,
    ),
    lettersRepopulate: normalizeBoolean(input.lettersRepopulate, base.lettersRepopulate),
    tileSpawnIntervalMs: normalizeNonNegativeInteger(
      input.tileSpawnIntervalMs,
      base.tileSpawnIntervalMs,
    ),
    meter: normalizeMeterSettings(input.meter, base.meter),
    minWordLength,
    redrawBoardAfterWord: normalizeBoolean(
      input.redrawBoardAfterWord,
      base.redrawBoardAfterWord,
    ),
    rotationEnabled: normalizeBoolean(input.rotationEnabled, base.rotationEnabled),
    scoreBonusMaxLength: normalizeScoreBonusMaxLength(
      input.scoreBonusMaxLength,
      base.scoreBonusMaxLength,
      minWordLength,
      maxWordLength,
    ),
    scoreMode: normalizeScoreMode(input.scoreMode, base.scoreMode),
    specialTiles: normalizeSpecialTileSettings(
      input.specialTiles,
      base.specialTiles,
      maxSpecialTileCount,
    ),
    skills: {
      copy: normalizeSkillSettings(input.skills?.copy, base.skills.copy),
      reset: normalizeResetSkillSettings(input.skills?.reset, base.skills.reset),
      shuffle: normalizeSkillSettings(input.skills?.shuffle, base.skills.shuffle),
      swap: normalizeSkillSettings(input.skills?.swap, base.skills.swap),
      zap: normalizeSkillSettings(input.skills?.zap, base.skills.zap),
    },
  }
}

export function getBoggleSkillUses(settings: BogglePlaygroundSkillSettings) {
  if (!settings.enabled) {
    return 0
  }

  return settings.uses === null ? BOGGLE_PLAYGROUND_UNLIMITED_USES : settings.uses
}

export function areBogglePlaygroundSettingsEqual(
  a: BogglePlaygroundSettings,
  b: BogglePlaygroundSettings,
) {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function getBoggleMaxSpecialTileCountForSettings(
  settings: Pick<BogglePlaygroundSettings, 'gridSize'>,
) {
  const { cols, rows } = getBoggleGridDimensions(settings.gridSize)
  return cols * rows
}

export function getBoggleMaxGoldCount(
  settings: Pick<BogglePlaygroundSettings, 'gridSize'>,
) {
  return getBoggleMaxSpecialTileCountForSettings(settings)
}
